import { useState, useEffect } from "react";
import { Plus, Trash2 } from "lucide-react";
import {
  FiEye,
  FiFilter,
  FiPlus,
  FiPlusCircle,
  FiRotateCcw,
  FiSearch,
} from "react-icons/fi";
import { useInteriorDesign } from "./useHook.js";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import GlobalPagination from "../../Components/Pagination/GlobalPagination";
import InteriorDesignModal from "../../Components/Modals/InteriorDesign/InteriorDesignModal.jsx";
import AddPopularInteriorDesign from "../../Components/Modals/PopularInteriorDesign/PopularInteriorDesign";
import SimilarInteriorDesignModal from "../../Components/Modals/SimilarInteriorDesign/SimilarInteriorDesign";
import RecordPosition from "../../Components/Modals/RecordPosition/RecordPosition";
import Loader from "../../Utils/loader";
import { useInteriorDesignCategory } from "../InteriorDesingCategories/useHook.js";
import { MdDelete } from "react-icons/md";
import { IoEye } from "react-icons/io5";

const InteriorDesign = () => {
  const navigate = useNavigate();
  const {
    getInteriorDesign,
    createInteriorDesign,
    updateInteriorDesign,
    deleteInteriorDesign,
    updateInteriorDesignPosition,
  } = useInteriorDesign();
  const { getAllInteriorDesignCategories } = useInteriorDesignCategory();

  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [interiorDesignData, setInteriorDesignData] = useState([]);
  const [offset, setOffset] = useState(0);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [showPopularDesignModal, setShowPopularDesignModal] = useState(false);
  const [isSimilarDesignModalOpen, setIsSimilarDesignModalOpen] =
    useState(false);
  const [searchFilter, setSearchFilter] = useState("");
  const [categoryName, setCategoryName] = useState("");
  const [isPositionModalOpen, setIsPositionModalOpen] = useState(false);
  const [selectedDesignForPosition, setSelectedDesignForPosition] =
    useState(null);
  const [interiorDesignCategories, setInteriorDesignCategories] = useState([]);

  useEffect(() => {
    const fetchCategories = async () => {
      setIsLoading(true);
      try {
        const categories = await getAllInteriorDesignCategories();
        setInteriorDesignCategories(categories || []);
        const defaultCategoryName =
          categories?.length > 0 ? categories[0].name : "";
        setCategoryName(defaultCategoryName);
        await getInteriorDesign(
          setInteriorDesignData,
          setTotal,
          0,
          limit,
          searchFilter,
          defaultCategoryName,
        );
      } finally {
        setIsLoading(false);
      }
    };
    fetchCategories();
  }, []);
  const handlePageChange = (newOffset) => {
    setOffset(newOffset);
    loadInteriorDesigns(newOffset, limit);
  };

  const loadInteriorDesigns = async (
    currentOffset = offset,
    currentLimit = limit,
  ) => {
    setIsLoading(true);
    try {
      await getInteriorDesign(
        setInteriorDesignData,
        setTotal,
        currentOffset,
        currentLimit,
        searchFilter,
        categoryName,
      );
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load interior designs. Please refresh the page.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (formData) => {
    setIsSubmitting(true);

    try {
      const apiData = new FormData();

      Object.keys(formData).forEach((key) => {
        if (
          key !== "images" &&
          key !== "imagePreviews" &&
          key !== "primaryImageIndex"
        ) {
          if (key === "features") {
            apiData.append(key, JSON.stringify(formData[key]));
          } else {
            apiData.append(key, formData[key]);
          }
        }
      });

      if (formData.images && formData.images.length > 0) {
        formData.images.forEach((image, index) => {
          apiData.append(`image`, image);
          const isPrimary = index === formData.primaryImageIndex;
          apiData.append(`isPrimary`, isPrimary);
        });
      }

      if (isEditing && formData.existingImages) {
        formData.existingImages.forEach((image) => {
          apiData.append("existing_images", JSON.stringify(image));
        });
      }

      let response;
      if (isEditing) {
        response = await updateInteriorDesign(editId, apiData);
      } else {
        response = await createInteriorDesign(apiData);
      }

      await loadInteriorDesigns(offset, limit);

      setShowModal(false);
      setIsEditing(false);
      setEditId(null);
      setEditData(null);

      Swal.fire({
        icon: "success",
        title: isEditing ? "Interior Design Updated" : "Interior Design Added",
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (error) {
      console.error("Failed to submit form:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to save interior design. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePositionClick = (design) => {
    setSelectedDesignForPosition(design);
    setIsPositionModalOpen(true);
  };

  const handleUpdatePosition = async (interiorDesignId, newPosition) => {
    await updateInteriorDesignPosition(interiorDesignId, newPosition);
    await loadInteriorDesigns(offset, limit);
  };

  const closePositionModal = () => {
    setIsPositionModalOpen(false);
    setSelectedDesignForPosition(null);
  };

  const handleEdit = async (id) => {
    try {
      const design = interiorDesignData.find((item) => item.id === id);
      if (design) {
        const transformedDesign = {
          ...design,
          existingImages: design.images || [],
        };
        setEditData(transformedDesign);
        setIsEditing(true);
        setEditId(id);
        setShowModal(true);
      }
    } catch (error) {
      console.error("Failed to load design for editing:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load design for editing. Please try again.",
      });
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel!",
      reverseButtons: true,
    });

    if (result.isConfirmed) {
      try {
        await deleteInteriorDesign(id);
        await loadInteriorDesigns(offset, limit);
        Swal.fire(
          "Deleted!",
          "Interior design deleted successfully.",
          "success",
        );
      } catch (error) {
        console.error("Failed to delete interior design:", error);
        Swal.fire(
          "Error!",
          "Failed to delete interior design. Please try again.",
          "error",
        );
      }
    }
  };

  const handlePageView = (id) => {
    navigate(`/interior-design/${id}`);
  };

  const handleAddNew = () => {
    setIsEditing(false);
    setEditId(null);
    setEditData(null);
    setShowModal(true);
  };

  const handleAddSimilarDesign = (id) => {
    setIsSimilarDesignModalOpen(id);
  };

  const handleSearch = async () => {
    setIsLoading(true);
    setOffset(0);
    try {
      await getInteriorDesign(
        setInteriorDesignData,
        setTotal,
        0,
        limit,
        searchFilter,
        categoryName,
      );
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to search interior designs. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setSearchFilter("");
    const defaultCategoryName =
      interiorDesignCategories?.length > 0
        ? interiorDesignCategories[0].name
        : "";
    setCategoryName(defaultCategoryName);
    setOffset(0);
    getInteriorDesign(
      setInteriorDesignData,
      setTotal,
      0,
      limit,
      "",
      defaultCategoryName,
    );
  };

  if (isLoading) {
    return <Loader message="Loading interior designs..." />;
  }

  return (
    <div>
      <div className="flex flex-wrap gap-4 justify-between items-center">
        <h3 className="text-xl font-bold">Interior Design</h3>

        <div className="flex flex-wrap gap-2">
          <button onClick={handleAddNew} className="btn btn-primary h-[44px]">
            <FiPlus className="text-white" />
            Add Interior Design
          </button>

          <button
            onClick={() => setShowPopularDesignModal(true)}
            className="btn btn-primary h-[44px]"
          >
            <FiPlus className="text-white" />
            Add Popular Design
          </button>
        </div>
      </div>

      <div className="bg-white p-5 w-full rounded-lg pagination-shadow flex flex-wrap xlCustom:flex-nowrap md:justify-end items-center gap-3 my-5">
        <div className="search-wrapper">
          <input
            type="text"
            className="search-input"
            placeholder="Search by name"
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
          />
          <FiSearch className="search-icon" />
        </div>
        <select
          className="status-select"
          value={categoryName}
          onChange={(e) => setCategoryName(e.target.value)}
        >
          {interiorDesignCategories.length === 0 ? (
            <option value="">Loading...</option>
          ) : (
            <option value="">All categories</option>
          )}
          {interiorDesignCategories.map((cat) => (
            <option key={cat.id} value={cat.name}>
              {cat.name}
            </option>
          ))}
        </select>
        <div className="flex flex-wrap md:flex-nowrap gap-2">
          <button className="btn btn-filter filter-btn" onClick={handleSearch}>
            <FiFilter className="text-white" /> Apply Filter
          </button>
          <button
            className="btn btn-reset-filter filter-btn"
            onClick={handleReset}
          >
            <FiRotateCcw className="text-white" /> Reset Filter
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white w-full rounded-lg pagination-shadow">
        <div className="overflow-x-auto">
          <table className="min-w-full table-auto bg-white rounded-lg  overflow-hidden">
            <thead>
              <tr className="brand-gradient text-white">
                <th className="table-th">Sr. No.</th>
                <th className="table-th">Title</th>
                <th className="table-th">Room Size</th>
                <th className="table-th">Category</th>
                {/* <th className="py-3 px-4 text-left">Design Type</th> */}
                <th className="table-th">Actions</th>
              </tr>
            </thead>
            <tbody>
              {interiorDesignData.length > 0 ? (
                interiorDesignData.map((design, index) => (
                  <tr
                    key={design.id}
                    className={`${
                      index % 2 === 0 ? "bg-gray-50" : "bg-gray-100"
                    } hover:bg-gray-200 transition-colors duration-300`}
                  >
                    <td className="table-td">
                      <button
                        type="button"
                        onClick={() => handlePositionClick(design)}
                        className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer font-medium"
                      >
                        {design.position ?? offset + index + 1}
                      </button>
                    </td>
                    <td className="table-td">{design.title}</td>
                    <td className="table-td">{design.room_size}</td>
                    <td className="table-td">{design.category?.name}</td>
                    {/* <td className="py-3 px-4">{design.design_type}</td> */}
                    <td className="table-td flex space-x-2">
                      <button
                        className="btn btn-action"
                        onClick={() => handleAddSimilarDesign(design.id)}
                      >
                        <FiPlusCircle className="text-white" />
                        Similar Design
                      </button>
                      <button
                        className="btn btn-view "
                        onClick={() => handlePageView(design.id)}
                      >
                        <IoEye className="text-white" /> View
                      </button>
                      <button
                        className="btn btn-delete text-white"
                        onClick={() => handleDelete(design.id)}
                      >
                        <MdDelete className="text-white" /> Delete
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="9"
                    className="py-4 px-4 text-center text-gray-500 font-bold"
                  >
                    No interior designs found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {interiorDesignData.length > 0 && total > limit && (
        <div className="mt-4">
          <GlobalPagination
            offset={offset}
            limit={limit}
            total={total}
            onPageChange={handlePageChange}
            className="bg-white"
          />
        </div>
      )}

      {/* Interior Design Modal */}
      <InteriorDesignModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setIsEditing(false);
          setEditId(null);
          setEditData(null);
        }}
        onSubmit={handleSubmit}
        isEditing={isEditing}
        editData={editData}
        isSubmitting={isSubmitting}
      />

      {/* Add Popular Interior Design Modal */}
      {showPopularDesignModal && (
        <AddPopularInteriorDesign
          isOpen={showPopularDesignModal}
          onClose={() => setShowPopularDesignModal(false)}
        />
      )}

      {/* Similar Interior Design Modal */}
      {isSimilarDesignModalOpen && (
        <SimilarInteriorDesignModal
          isOpen={isSimilarDesignModalOpen}
          onClose={() => setIsSimilarDesignModalOpen(false)}
          designId={isSimilarDesignModalOpen}
        />
      )}

      {isPositionModalOpen && selectedDesignForPosition && (
        <RecordPosition
          isOpen={isPositionModalOpen}
          onClose={closePositionModal}
          currentPosition={selectedDesignForPosition.position ?? 0}
          recordId={selectedDesignForPosition.id}
          onUpdatePosition={handleUpdatePosition}
          moduleName="Interior Design"
        />
      )}
    </div>
  );
};

export default InteriorDesign;
